import { formatMoney } from './format.js';

// Global display currency. A workspace-wide setting (header selector) that
// controls the currency monetary values are *shown* in. Conversions go via
// EUR, the platform's normalisation base (the universe is defined by a €1M
// turnover threshold), so an amount known in EUR can be rendered in any of
// these.

export type GlobalCurrency = 'EUR' | 'GBP' | 'NOK' | 'DKK' | 'SEK' | 'CHF' | 'PLN' | 'CZK' | 'RON';

export const GLOBAL_CURRENCIES: readonly GlobalCurrency[] = [
  'EUR',
  'GBP',
  'NOK',
  'DKK',
  'SEK',
  'CHF',
  'PLN',
  'CZK',
  'RON',
];

export const DEFAULT_GLOBAL_CURRENCY: GlobalCurrency = 'EUR';

export const CURRENCY_SYMBOL: Record<GlobalCurrency, string> = {
  EUR: '€',
  GBP: '£',
  NOK: 'kr',
  DKK: 'kr',
  SEK: 'kr',
  CHF: 'CHF',
  PLN: 'zł',
  CZK: 'Kč',
  RON: 'lei',
};

/** Symbols for every storage-side currency (display set + HUF/BGN). */
export const NATIVE_CURRENCY_SYMBOL: Record<NativeCurrency, string> = {
  ...CURRENCY_SYMBOL,
  HUF: 'Ft',
  BGN: 'лв',
};

// Storage-side currencies that are NOT offered as display currencies. HUF and
// BGN denominate Hungary's and Bulgaria's stored figures (filing_currency in
// merge-staging.ts) but nobody asks to *view* the workspace in them, so they
// extend the conversion tables without widening the header selector.
export type NativeCurrency = GlobalCurrency | 'HUF' | 'BGN';

// rate_to_eur: 1 unit of currency = N EUR (so EUR_amount = ccy_amount × rate,
// and ccy_amount = EUR_amount / rate). Values mirror the platform `fx_rates`
// table (latest annual rates). Display-only — the actual scope/filtering is
// done server-side on turnover_eur — so a small drift here is cosmetic; bump
// these when fx_rates is refreshed, or wire to an /fx-rates endpoint later.
export const RATE_TO_EUR: Record<NativeCurrency, number> = {
  EUR: 1,
  GBP: 1.152083,
  NOK: 0.089459,
  DKK: 0.133837,
  SEK: 0.092879,
  CHF: 1.06383, // 1 EUR ≈ 0.94 CHF
  PLN: 0.232558, // 1 EUR ≈ 4.30 PLN
  CZK: 0.04, // 1 EUR ≈ 25.0 CZK
  RON: 0.201207, // 1 EUR ≈ 4.97 RON
  HUF: 0.002532, // 1 EUR ≈ 395 HUF
  BGN: 0.511292, // fixed peg — 1 EUR = 1.95583 BGN
};

const NATIVE_CURRENCIES: readonly NativeCurrency[] = [...GLOBAL_CURRENCIES, 'HUF', 'BGN'];

// The native currency each market's stored monetary values are denominated in
// — ALL 27 served markets (mirrors filing_currency in merge-staging.ts).
// Platform metrics/financials are kept in the entity's local currency; the
// global-currency selector rebases them at display time via EUR. A market
// missing from this map silently rendered UNCONVERTED and symbol-less (the
// pre-27 bug: HUF billions sorted against EUR millions), so keep it complete.
export const NATIVE_CURRENCY_BY_COUNTRY: Record<string, NativeCurrency> = {
  AT: 'EUR',
  BE: 'EUR',
  BG: 'BGN',
  CH: 'CHF',
  CZ: 'CZK',
  DE: 'EUR',
  DK: 'DKK',
  EE: 'EUR',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GB: 'GBP',
  GR: 'EUR',
  HR: 'EUR',
  HU: 'HUF',
  IE: 'EUR',
  IT: 'EUR',
  LT: 'EUR',
  LV: 'EUR',
  NL: 'EUR',
  NO: 'NOK',
  PL: 'PLN',
  PT: 'EUR',
  RO: 'RON',
  SE: 'SEK',
  SI: 'EUR',
  SK: 'EUR',
};

// Live rates, seeded from the bundled fallback above and overlaid at runtime
// from /fx-rates/latest (the worker refreshes that weekly from ECB/Frankfurter).
// A pure converter can't read a React hook, so the active rates live in this
// module cell that useFxRatesSync updates; any currency the feed omits keeps
// its bundled value, and if the feed never loads everything still works.
let activeRates: Record<NativeCurrency, number> = { ...RATE_TO_EUR };

/** Overlay live latest-spot rates (from /fx-rates/latest). Keeps the bundled
 *  value for any currency the feed omits or returns non-positive. */
export function setActiveFxRates(rates: Partial<Record<string, number>>): void {
  const next: Record<NativeCurrency, number> = { ...RATE_TO_EUR };
  for (const c of NATIVE_CURRENCIES) {
    const r = rates[c];
    if (typeof r === 'number' && Number.isFinite(r) && r > 0) next[c] = r;
  }
  activeRates = next;
}

/** The rate currently in effect for a currency (live if loaded, else bundled). */
export function rateToEur(ccy: NativeCurrency): number {
  return activeRates[ccy] ?? RATE_TO_EUR[ccy];
}

/** Convert an amount known in EUR into the given currency. */
export function convertFromEur(amountEur: number, ccy: GlobalCurrency): number {
  return amountEur / rateToEur(ccy);
}

/** Convert an amount from one (native) currency to another (bridged via EUR). */
export function convertCurrency(amount: number, from: NativeCurrency, to: GlobalCurrency): number {
  if (from === to) return amount;
  return (amount * rateToEur(from)) / rateToEur(to);
}

/** Format an EUR amount in the given currency (symbol + compact body). */
export function formatEurInCurrency(amountEur: number, ccy: GlobalCurrency): string {
  return formatMoney(convertFromEur(amountEur, ccy), { currency: CURRENCY_SYMBOL[ccy] });
}
